import { FormEvent, useState } from 'react'
import { Alert, Button, Form, InputGroup, Modal, Spinner, Table } from 'react-bootstrap'
import { readError } from '@/services/apiClient'
import { searchCustomers, type CustomerResult } from '@/services/customerService'

export function CustomerSearchDialog({
  title,
  confirmLabel,
  onClose,
  onSelect,
}: {
  title: string
  confirmLabel: string
  onClose: () => void
  onSelect: (customer: CustomerResult) => void
}) {
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<CustomerResult[] | null>(null)
  const [selectedId, setSelectedId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function search(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    setSelectedId('')
    try {
      setRows(await searchCustomers({ search: query }))
    } catch (caught) {
      setRows(null)
      setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  const selected = rows?.find((row) => row.id === selectedId)

  return (
    <Modal show onHide={() => { if (!busy) onClose() }} backdrop={busy ? 'static' : true} keyboard={!busy} size="lg" dialogClassName="customer-search-dialog">
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{title} - Customer search</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={(event) => void search(event)}>
          <InputGroup className="customer-search-bar">
            <Form.Control
              autoFocus
              value={query}
              placeholder="customer/address/contact name..."
              aria-label="Search customers"
              disabled={busy}
              onChange={(event) => setQuery(event.target.value)}
            />
            <Button type="submit" disabled={busy}>
              {busy && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
              Search
            </Button>
          </InputGroup>
        </Form>
        <div role="status" aria-live="polite">
          {busy && <span className="visually-hidden">Searching for customers…</span>}
        </div>
        {error && <Alert className="mt-3" variant="danger">{error}</Alert>}
        {!busy && rows && !rows.length && <p className="mt-3 mb-0">No customers match that search.</p>}
        {!busy && rows && rows.length > 0 && (
          <div className="customer-search-results">
          <Table hover className="mb-0">
            <thead>
              <tr>
                <th />
                <th>Customer</th>
                <th>Account</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} onClick={() => setSelectedId(row.id)} style={{ cursor: 'pointer' }}>
                  <td>
                    <Form.Check
                      type="radio"
                      name="selected-customer"
                      aria-label={row.name}
                      checked={selectedId === row.id}
                      onChange={() => setSelectedId(row.id)}
                    />
                  </td>
                  <td>{row.name}</td>
                  <td>{row.accountNumber}</td>
                  <td>{[row.city, row.postcode].filter(Boolean).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cancel</Button>
        <Button disabled={busy || !selected} onClick={() => !busy && selected && onSelect(selected)}>{confirmLabel}</Button>
      </Modal.Footer>
    </Modal>
  )
}
